import { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy, Delete } from '@mui/icons-material';
import { Formik, FormikHelpers } from 'formik';
import { object, string } from 'yup';
import { deleteField, serverTimestamp } from 'firebase/firestore';
import {
  createAttendee,
  softDeleteAttendee,
  updateAttendee,
  useActor,
} from '../lib/audited-writes';
import { useFirebaseContext } from '../lib/firebase-context';
import { formatGroupShort, sortGroups } from '../lib/groups';
import { useToast } from '../lib/toast-context';
import {
  ATTENDEE_NAME_MAX_LENGTH,
  EMERGENCY_CONTACT_NAME_MAX_LENGTH,
  EMERGENCY_CONTACT_PHONE_MAX_LENGTH,
  EMERGENCY_CONTACT_RELATIONSHIP_MAX_LENGTH,
  GRADE_LABELS,
  GRADES,
} from '../lib/constants';
import { Attendee, Grade } from '../lib/types';
import { useUserContext } from '../lib/users-context';
import { FLAG_MAX_LENGTH, flagDetail } from '../lib/attendee-flags';
import { DOB_REGEX, ageFromDob } from '../lib/age';
import { SHIRT_SIZE_OPTIONS } from '../lib/shirt-size';

type Input = {
  first_name: string;
  last_name: string;
  groupRef: string;
  birthdate: string;
  grade_completed: '' | Grade;
  gender: '' | 'Male' | 'Female';
  // Stored verbatim on the Attendee doc — VBS-owned but editable here
  // so admins can fix typos and supply values for manually-added kids.
  // Like `gender`, editing it trips the `locally_edited_at` lock so VBS
  // sync won't clobber the local edit.
  tshirt_size: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  emergency_contact_relationship: string;
  notes: string;
  medical_concerns: string;
  food_allergies: string;
  customFieldValues: Record<string, string>;
};

const blankValues: Input = {
  first_name: '',
  last_name: '',
  groupRef: '',
  birthdate: '',
  grade_completed: '',
  gender: '',
  tshirt_size: '',
  emergency_contact_name: '',
  emergency_contact_phone: '',
  emergency_contact_relationship: '',
  notes: '',
  medical_concerns: '',
  food_allergies: '',
  customFieldValues: {},
};

// Strips non-digits and re-inserts slashes so the user can type just the
// numbers and the field auto-formats as mm/dd/yyyy. Caps at 8 digits.
const formatDob = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length >= 5) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  }
  if (digits.length >= 3) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  return digits;
};

const NOTES_MAX_LENGTH = 500;

const AttendeeForm = ({
  attendee,
  onClose,
}: {
  attendee: Attendee | null;
  onClose: () => void;
}) => {
  const actor = useActor();
  const { groups, activeEvent, checkInDates, customFields, removeAttendeeLocally } =
    useFirebaseContext();
  const { isAdmin } = useUserContext();
  const toast = useToast();
  const isEdit = attendee !== null;
  const sortedGroups = sortGroups(groups);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCopyRef = async () => {
    if (!attendee) return;
    try {
      await navigator.clipboard.writeText(attendee.ref);
      toast.success('Registration ID copied');
    } catch (error) {
      toast.error('Could not copy Registration ID');
      console.error('Could not copy Registration ID', error);
    }
  };

  // Soft-delete: matches the existing query filter
  // (`where("is_deleted", "==", false)`) so the attendee disappears from
  // every list while their check-in history is preserved. We also drop the
  // attendee from the in-memory context immediately so the UI updates
  // before the Firestore snapshot has propagated.
  const handleDelete = async () => {
    if (!attendee) return;
    // Mid-session safety: if the admin flipped the active event while this
    // dialog was open, the doc we're about to soft-delete no longer belongs
    // to the event the user is viewing. Bail rather than silently mutate a
    // doc the user can't see anymore.
    if (!activeEvent || attendee.eventRef !== activeEvent.ref) {
      toast.error('Active event changed — close and reopen this attendee');
      return;
    }
    setIsDeleting(true);
    try {
      await softDeleteAttendee(actor, attendee, checkInDates);
      removeAttendeeLocally(attendee.ref);
      toast.success('Attendee deleted');
      setConfirmDelete(false);
      onClose();
    } catch (error) {
      toast.error('Error deleting attendee');
      console.error('Error deleting attendee', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSubmit = async (
    values: Input,
    { setSubmitting }: FormikHelpers<Input>
  ) => {
    try {
      const trimmedEmergencyName = values.emergency_contact_name.trim();
      const trimmedEmergencyPhone = values.emergency_contact_phone.trim();
      const trimmedEmergencyRelationship =
        values.emergency_contact_relationship.trim();
      const trimmedNotes = values.notes.trim();
      const trimmedBirthdate = values.birthdate.trim();
      const trimmedMedical = values.medical_concerns.trim();
      const trimmedFood = values.food_allergies.trim();
      const trimmedShirt = values.tshirt_size.trim();
      // Map (none) selections to deleteField() so we don't accumulate empty
      // strings inside the customFields map. Only includes keys for fields
      // that currently exist; deleted fields are wiped server-side by
      // deleteCustomField, not here. Skips no-op writes so the audit log
      // doesn't get a spurious `from: null → to: <unchanged>` entry for
      // every defined field on every save.
      const customFieldsPayload: Record<string, unknown> = {};
      if (isEdit) {
        for (const f of customFields) {
          const v = (values.customFieldValues?.[f.ref] ?? '').trim();
          const prev = (attendee?.customFields?.[f.ref] ?? '').trim();
          if (v === prev) continue;
          customFieldsPayload[`customFields.${f.ref}`] = v ? v : deleteField();
        }
      }
      if (isEdit) {
        // Same mid-session safety as handleDelete: refuse to apply edits to
        // a doc that no longer belongs to the active event.
        if (!activeEvent || attendee.eventRef !== activeEvent.ref) {
          toast.error('Active event changed — close and reopen this attendee');
          return;
        }

        // Did the user change any VBS-owned field? Only those changes should
        // stamp `locally_edited_at` — that flag tells VBS sync "don't overwrite
        // these on a normal sync". Group changes and Notes are check-in-owned
        // (VBS never writes them), so editing only those should NOT lock the
        // record, otherwise legitimate VBS updates (e.g. allergy correction)
        // would silently fail to propagate.
        const vbsOwnedChanged =
          values.first_name !== attendee.first_name ||
          values.last_name !== attendee.last_name ||
          trimmedBirthdate !== (attendee.birthdate || '') ||
          values.gender !== (attendee.gender || '') ||
          trimmedShirt !== (attendee.tshirt_size || '') ||
          trimmedEmergencyName !== (attendee.emergency_contact_name || '') ||
          trimmedEmergencyPhone !== (attendee.emergency_contact_phone || '') ||
          trimmedEmergencyRelationship !==
            (attendee.emergency_contact_relationship || '') ||
          trimmedMedical !== flagDetail(attendee.medical_concerns) ||
          trimmedFood !== flagDetail(attendee.food_allergies);

        await updateAttendee(actor, attendee, {
          first_name: values.first_name,
          last_name: values.last_name,
          groupRef: values.groupRef || deleteField(),
          emergency_contact_name: trimmedEmergencyName || deleteField(),
          emergency_contact_phone: trimmedEmergencyPhone || deleteField(),
          emergency_contact_relationship:
            trimmedEmergencyRelationship || deleteField(),
          notes: trimmedNotes,
          birthdate: trimmedBirthdate || deleteField(),
          gender: values.gender || deleteField(),
          tshirt_size: trimmedShirt || deleteField(),
          medical_concerns: trimmedMedical || deleteField(),
          food_allergies: trimmedFood || deleteField(),
          // `s3LabelUrl` is wiped centrally by `updateAttendee` on every
          // edit, so it intentionally isn't listed here.
          ...customFieldsPayload,
          ...(vbsOwnedChanged
            ? { locally_edited_at: serverTimestamp() }
            : {}),
        });
        toast.success('Attendee updated');
      } else {
        if (!activeEvent) {
          toast.error('No active event — set one in Admin first');
          return;
        }
        // Manually-added attendees use a Firestore-generated ID (not a VBS
        // child UUID) so they never collide with synced records. Setting the
        // lock here is harmless — VBS sync targets different doc IDs anyway.
        const input: Record<string, unknown> = {
          eventRef: activeEvent.ref,
          first_name: values.first_name,
          last_name: values.last_name,
          is_deleted: false,
          ui_added: true,
          locally_edited_at: serverTimestamp(),
        };
        if (values.groupRef) input.groupRef = values.groupRef;
        if (trimmedEmergencyName) {
          input.emergency_contact_name = trimmedEmergencyName;
        }
        if (trimmedEmergencyPhone) {
          input.emergency_contact_phone = trimmedEmergencyPhone;
        }
        if (trimmedEmergencyRelationship) {
          input.emergency_contact_relationship = trimmedEmergencyRelationship;
        }
        if (trimmedNotes) input.notes = trimmedNotes;
        if (trimmedBirthdate) input.birthdate = trimmedBirthdate;
        if (trimmedMedical) input.medical_concerns = trimmedMedical;
        if (trimmedFood) input.food_allergies = trimmedFood;
        if (values.grade_completed) {
          input.grade_completed = values.grade_completed;
        }
        if (values.gender) input.gender = values.gender;
        if (trimmedShirt) input.tshirt_size = trimmedShirt;
        const newCustomFields: Record<string, string> = {};
        for (const f of customFields) {
          const v = (values.customFieldValues?.[f.ref] ?? '').trim();
          if (v) newCustomFields[f.ref] = v;
        }
        if (Object.keys(newCustomFields).length > 0) {
          input.customFields = newCustomFields;
        }
        await createAttendee(actor, input);
        toast.success('New attendee added');
      }
      onClose();
    } catch (error) {
      const errMsg = isEdit
        ? 'Error editing attendee'
        : 'Error adding new attendee';
      toast.error(errMsg);
      console.error(errMsg, error);
    } finally {
      setSubmitting(false);
    }
  };

  const initialValues: Input = attendee
    ? {
        first_name: attendee.first_name,
        last_name: attendee.last_name,
        groupRef: attendee.groupRef || '',
        birthdate: attendee.birthdate || '',
        grade_completed: attendee.grade_completed || '',
        gender: attendee.gender || '',
        tshirt_size: attendee.tshirt_size || '',
        emergency_contact_name: attendee.emergency_contact_name || '',
        emergency_contact_phone: attendee.emergency_contact_phone || '',
        emergency_contact_relationship:
          attendee.emergency_contact_relationship || '',
        notes: attendee.notes || '',
        // Legacy 'Yes' / 'No' placeholders shouldn't pre-fill the new free-text
        // input — flagDetail strips them so the user starts with a blank field.
        medical_concerns: flagDetail(attendee.medical_concerns),
        food_allergies: flagDetail(attendee.food_allergies),
        customFieldValues: customFields.reduce<Record<string, string>>(
          (acc, f) => {
            acc[f.ref] = attendee.customFields?.[f.ref] ?? '';
            return acc;
          },
          {},
        ),
      }
    : {
        ...blankValues,
        customFieldValues: customFields.reduce<Record<string, string>>(
          (acc, f) => {
            acc[f.ref] = '';
            return acc;
          },
          {},
        ),
      };

  return (
    <>
    <Formik
      validateOnBlur
      initialValues={initialValues}
      validationSchema={object().shape({
        first_name: string()
          .required('First name is required')
          .max(
            ATTENDEE_NAME_MAX_LENGTH,
            `Must be ${ATTENDEE_NAME_MAX_LENGTH} characters or less`
          ),
        last_name: string()
          .required('Last name is required')
          .max(
            ATTENDEE_NAME_MAX_LENGTH,
            `Must be ${ATTENDEE_NAME_MAX_LENGTH} characters or less`
          ),
        birthdate: string().matches(DOB_REGEX, {
          message: 'Use mm/dd/yyyy',
          excludeEmptyString: true,
        }),
        grade_completed: string().oneOf(['', ...GRADES], 'Invalid grade'),
        gender: string().oneOf(['', 'Male', 'Female'], 'Invalid gender'),
        emergency_contact_name: string().max(
          EMERGENCY_CONTACT_NAME_MAX_LENGTH,
          `Must be ${EMERGENCY_CONTACT_NAME_MAX_LENGTH} characters or less`
        ),
        emergency_contact_phone: string().max(
          EMERGENCY_CONTACT_PHONE_MAX_LENGTH,
          `Must be ${EMERGENCY_CONTACT_PHONE_MAX_LENGTH} characters or less`
        ),
        emergency_contact_relationship: string().max(
          EMERGENCY_CONTACT_RELATIONSHIP_MAX_LENGTH,
          `Must be ${EMERGENCY_CONTACT_RELATIONSHIP_MAX_LENGTH} characters or less`
        ),
        notes: string().max(
          NOTES_MAX_LENGTH,
          `Must be ${NOTES_MAX_LENGTH} characters or less`
        ),
        medical_concerns: string().max(
          FLAG_MAX_LENGTH,
          `Must be ${FLAG_MAX_LENGTH} characters or less`
        ),
        food_allergies: string().max(
          FLAG_MAX_LENGTH,
          `Must be ${FLAG_MAX_LENGTH} characters or less`
        ),
      })}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleSubmit,
        isSubmitting,
        errors,
        touched,
        handleReset,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'flex', flexDirection: 'column', my: 2 }}>
            {isEdit && attendee && (
              <TextField
                label='Registration ID'
                value={attendee.ref}
                size='small'
                sx={{ mb: 2 }}
                InputProps={{
                  readOnly: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  endAdornment: (
                    <InputAdornment position='end'>
                      <Tooltip title='Copy'>
                        <IconButton
                          edge='end'
                          size='small'
                          onClick={handleCopyRef}
                          aria-label='Copy registration ID'
                        >
                          <ContentCopy fontSize='small' />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label='First name *'
                name='first_name'
                value={values.first_name}
                onChange={handleChange}
                error={Boolean(errors.first_name)}
                helperText={errors.first_name}
                inputProps={{ maxLength: ATTENDEE_NAME_MAX_LENGTH }}
                fullWidth
              />
              <TextField
                label='Last name *'
                name='last_name'
                value={values.last_name}
                onChange={handleChange}
                error={Boolean(errors.last_name)}
                helperText={errors.last_name}
                inputProps={{ maxLength: ATTENDEE_NAME_MAX_LENGTH }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label='Date of birth (optional)'
                name='birthdate'
                value={values.birthdate}
                onChange={(e) =>
                  setFieldValue('birthdate', formatDob(e.target.value))
                }
                placeholder='mm/dd/yyyy'
                inputProps={{
                  inputMode: 'numeric',
                  maxLength: 10,
                  autoComplete: 'off',
                }}
                error={Boolean(errors.birthdate)}
                // While the user is typing an invalid / partial DOB we show
                // the Yup error; once it parses cleanly we swap to a friendly
                // age readout so volunteers can sanity-check the kid's age.
                helperText={(() => {
                  if (errors.birthdate) return errors.birthdate;
                  const age = ageFromDob(values.birthdate);
                  return age !== null ? `${age} y.o.` : undefined;
                })()}
                fullWidth
              />
              {/* `gender` is VBS-owned but editable here so manually-added
                  attendees can be added with the right value (auto-assign's
                  gender-balancing reads it). On synced records the
                  locally_edited_at lock keeps the edit from being clobbered
                  by the next sync. Sized narrow to stay "tiny" alongside
                  DOB/Shirt on the same row. */}
              <FormControl sx={{ minWidth: 130 }}>
                <InputLabel id='gender-select-label'>
                  Gender (optional)
                </InputLabel>
                <Select
                  labelId='gender-select-label'
                  name='gender'
                  value={values.gender}
                  onChange={handleChange}
                  label='Gender (optional)'
                >
                  <MenuItem value=''>
                    <em>(none)</em>
                  </MenuItem>
                  <MenuItem value='Male'>Male</MenuItem>
                  <MenuItem value='Female'>Female</MenuItem>
                </Select>
              </FormControl>
              {/* `tshirt_size` is VBS-owned but editable here so admins
                  can fix typos / set values on manually-added kids. The
                  locally_edited_at lock keeps the edit from being
                  clobbered by the next VBS sync. Options live in
                  `SHIRT_SIZE_OPTIONS` (`src/lib/shirt-size.ts`) — keep
                  that array in sync with whatever VBS actually sends.
                  An "orphan" doc value that isn't in the known list
                  (e.g. a new VBS option we haven't added yet) is
                  rendered as an extra MenuItem so the picker doesn't
                  go blank on edit. */}
              {(() => {
                const knownOptions = SHIRT_SIZE_OPTIONS as readonly string[];
                const isOrphan =
                  values.tshirt_size !== '' &&
                  !knownOptions.includes(values.tshirt_size);
                return (
                  <FormControl fullWidth>
                    <InputLabel id='tshirt-size-select-label'>
                      T-shirt size (optional)
                    </InputLabel>
                    <Select
                      labelId='tshirt-size-select-label'
                      name='tshirt_size'
                      value={values.tshirt_size}
                      onChange={handleChange}
                      label='T-shirt size (optional)'
                    >
                      <MenuItem value=''>
                        <em>(none)</em>
                      </MenuItem>
                      {SHIRT_SIZE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                      {isOrphan && (
                        <MenuItem value={values.tshirt_size}>
                          {values.tshirt_size}
                        </MenuItem>
                      )}
                    </Select>
                  </FormControl>
                );
              })()}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {/* VBS owns `grade_completed` for synced attendees, so the
                  field is editable only on the add path. In edit mode we
                  show it disabled — volunteers correct grades in VBS. */}
              <FormControl fullWidth disabled={isEdit}>
                <InputLabel id='grade-select-label'>
                  Grade completed (optional)
                </InputLabel>
                <Select
                  labelId='grade-select-label'
                  name='grade_completed'
                  value={values.grade_completed}
                  onChange={handleChange}
                  label='Grade completed (optional)'
                >
                  <MenuItem value=''>
                    <em>(none)</em>
                  </MenuItem>
                  {GRADES.map((g) => (
                    <MenuItem key={g} value={g}>
                      {GRADE_LABELS[g]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id='group-select-label'>Group (optional)</InputLabel>
                <Select
                  labelId='group-select-label'
                  name='groupRef'
                  value={values.groupRef}
                  onChange={handleChange}
                  label='Group (optional)'
                >
                  <MenuItem value=''>
                    <em>(unassigned)</em>
                  </MenuItem>
                  {sortedGroups.map((group) => (
                    <MenuItem key={group.ref} value={group.ref}>
                      {formatGroupShort(group)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label='Emergency contact name (optional)'
              name='emergency_contact_name'
              value={values.emergency_contact_name}
              onChange={handleChange}
              inputProps={{
                maxLength: EMERGENCY_CONTACT_NAME_MAX_LENGTH,
              }}
              sx={{ mb: 2 }}
              error={Boolean(errors.emergency_contact_name)}
              helperText={errors.emergency_contact_name}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField
                label='Phone (optional)'
                name='emergency_contact_phone'
                value={values.emergency_contact_phone}
                onChange={handleChange}
                inputProps={{
                  inputMode: 'tel',
                  autoComplete: 'tel',
                  maxLength: EMERGENCY_CONTACT_PHONE_MAX_LENGTH,
                }}
                error={Boolean(errors.emergency_contact_phone)}
                helperText={errors.emergency_contact_phone}
                sx={{ flex: 1 }}
              />
              <TextField
                label='Relationship (optional)'
                name='emergency_contact_relationship'
                value={values.emergency_contact_relationship}
                onChange={handleChange}
                inputProps={{
                  maxLength: EMERGENCY_CONTACT_RELATIONSHIP_MAX_LENGTH,
                }}
                error={Boolean(errors.emergency_contact_relationship)}
                helperText={errors.emergency_contact_relationship}
                sx={{ flex: 1 }}
              />
            </Box>
            <TextField
              label='Notes (optional)'
              name='notes'
              value={values.notes}
              onChange={handleChange}
              multiline
              minRows={3}
              inputProps={{ maxLength: NOTES_MAX_LENGTH }}
              sx={{ mb: 2 }}
              error={touched.notes && Boolean(errors.notes)}
              helperText={
                (touched.notes && errors.notes) ||
                `${values.notes.length}/${NOTES_MAX_LENGTH}`
              }
            />
            <TextField
              label='Medical concerns (optional)'
              name='medical_concerns'
              value={values.medical_concerns}
              onChange={handleChange}
              inputProps={{ maxLength: FLAG_MAX_LENGTH }}
              sx={{ mb: 2 }}
              error={touched.medical_concerns && Boolean(errors.medical_concerns)}
              helperText={
                (touched.medical_concerns && errors.medical_concerns) ||
                `${values.medical_concerns.length}/${FLAG_MAX_LENGTH}`
              }
            />
            <TextField
              label='Food allergies (optional)'
              name='food_allergies'
              value={values.food_allergies}
              onChange={handleChange}
              inputProps={{ maxLength: FLAG_MAX_LENGTH }}
              sx={{ mb: 2 }}
              error={touched.food_allergies && Boolean(errors.food_allergies)}
              helperText={
                (touched.food_allergies && errors.food_allergies) ||
                `${values.food_allergies.length}/${FLAG_MAX_LENGTH}`
              }
            />
            {customFields.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Custom fields
                </Typography>
                <Stack spacing={2}>
                  {customFields.map((f) => {
                    const currentValue =
                      values.customFieldValues?.[f.ref] ?? '';
                    // Stored attendee values are kept verbatim; we never
                    // rewrite them when an option is renamed/removed. If
                    // the stored value isn't in the field's current
                    // option list, surface it as a disabled MenuItem so
                    // volunteers see "Red (no longer an option)" instead
                    // of a silently-blank Select they're tempted to save.
                    const isOrphan =
                      currentValue !== '' &&
                      !f.options.includes(currentValue);
                    return (
                      <FormControl key={f.ref} fullWidth size='small'>
                        <InputLabel id={`custom-field-${f.ref}-label`}>
                          {f.name}
                        </InputLabel>
                        <Select
                          labelId={`custom-field-${f.ref}-label`}
                          label={f.name}
                          value={currentValue}
                          onChange={(e) =>
                            setFieldValue(
                              `customFieldValues.${f.ref}`,
                              e.target.value
                            )
                          }
                        >
                          <MenuItem value=''>
                            <em>(none)</em>
                          </MenuItem>
                          {f.options.map((option) => (
                            <MenuItem key={option} value={option}>
                              {option}
                            </MenuItem>
                          ))}
                          {isOrphan && (
                            <MenuItem disabled value={currentValue}>
                              {currentValue} (no longer an option)
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    );
                  })}
                </Stack>
              </Box>
            )}
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box>
                {isEdit && isAdmin && (
                  <Button
                    type='button'
                    color='error'
                    variant='outlined'
                    startIcon={<Delete />}
                    disabled={isSubmitting}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete
                  </Button>
                )}
              </Box>
              <ButtonGroup>
                <Button
                  color='error'
                  type='button'
                  disabled={isSubmitting}
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  color='secondary'
                  type='reset'
                  disabled={isSubmitting}
                  onClick={handleReset}
                >
                  Reset
                </Button>
                <Button type='submit' disabled={isSubmitting} color='primary'>
                  {isEdit ? 'Save' : 'Add'}
                </Button>
              </ButtonGroup>
            </Box>
          </Box>
        </form>
      )}
    </Formik>

    <Dialog
      open={confirmDelete}
      onClose={() => !isDeleting && setConfirmDelete(false)}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>Delete attendee</DialogTitle>
      <DialogContent>
        {attendee && (
          <DialogContentText>
            Delete{' '}
            <strong>
              {attendee.first_name} {attendee.last_name}
            </strong>
            ? They will be removed from all lists. Their check-in history is
            retained.
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          type='button'
          onClick={() => setConfirmDelete(false)}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          type='button'
          color='error'
          variant='contained'
          onClick={handleDelete}
          disabled={isDeleting}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default AttendeeForm;
